import { Helmet } from 'react-helmet-async'
import { FadeIn } from '@components/animations/FadeIn/FadeIn'
import { Award, Users, Target, Briefcase, Building2, Flag, Code2, Globe2, TrendingUp, Sparkles, Star, Shield } from 'lucide-react'

const timeline = [
  {
    year: '2009',
    title: 'Entered Government Projects',
    description: 'Started journey in Government Tenders, Public Sector Projects and Institutional Solutions.',
    icon: Building2,
    color: '#6366f1',
  },
  {
    year: '2010',
    title: 'Founded Organization',
    description: 'Established the foundation of the company with a vision to deliver high-quality government and enterprise solutions.',
    icon: Flag,
    color: '#8b5cf6',
  },
  {
    year: '2016',
    title: 'Technology Division Established',
    description: 'Expanded into Software Development, Digital Transformation, Cloud and IT Solutions.',
    icon: Code2,
    color: '#ec4899',
  },
  {
    year: '2020',
    title: 'Digital Governance & Smart Solutions',
    description: 'Successfully executed multiple Government Digital Transformation initiatives across Maharashtra.',
    icon: Globe2,
    color: '#06b6d4',
  },
  {
    year: '2025',
    title: '₹150 Crore Enterprise',
    description: 'Evolved into a multi-sector enterprise delivering Technology, Infrastructure, Healthcare, Education and Smart Governance solutions.',
    icon: TrendingUp,
    color: '#10b981',
  },
]

const achievements = [
  { icon: Award, value: '15+', label: 'Years of Leadership', color: '#6366f1' },
  { icon: Target, value: '₹150Cr', label: 'Enterprise Value', color: '#8b5cf6' },
  { icon: Briefcase, value: '100+', label: 'Government Projects', color: '#ec4899' },
]

const highlights = [
  { icon: Users, value: '50+', label: 'Government Clients' },
  { icon: Shield, value: '10', label: 'ISO Certifications' },
  { icon: Star, value: '5', label: 'Star Rated' },
  { icon: Award, value: '14+', label: 'Years Experience' },
]

const MDMessage = () => {
  return (
    <>
      <Helmet>
        <title>Managing Director Message | Eonixa Dynamics</title>
        <meta name="description" content="Message from Mr. Faheem Shaikh, Co-Founder & Managing Director of Eonixa Dynamics Pvt Ltd - Visionary entrepreneur with 15+ years experience, driving government projects across Maharashtra and pan-India" />
      </Helmet>

      <section className="min-h-screen relative overflow-hidden">
        <div className="container-enterprise relative z-10 py-20 lg:py-24">
          {/* Page Header */}
          <FadeIn>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
                style={{
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                  border: '1px solid rgba(99, 102, 241, 0.25)',
                  boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
                }}
              >
                <Award className="w-5 h-5 text-primary" />
                <span className="text-sm text-slate-300">Leadership Profile</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                Message from Managing Director
              </h1>
              <p className="text-lg text-slate-400 max-w-2xl mx-auto">
                Vision for transforming Government Technology and Infrastructure in India
              </p>
            </div>
          </FadeIn>

          {/* Main Content Grid */}
          <div className="grid gap-8 lg:grid-cols-2 items-start">
            {/* LEFT SIDE - Leadership Journey Timeline */}
            <FadeIn delay={0.1}>
              <div className="relative">
                {/* Timeline Header */}
                <div className="mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
                    style={{
                      background: 'rgba(99, 102, 241, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                    }}
                  >
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="text-sm text-slate-300">Leadership Journey</span>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">
                    Our Growth Story
                  </h2>
                  <p className="text-slate-400">
                    From humble beginnings to a ₹150 Crore enterprise
                  </p>
                </div>

                {/* Vertical Timeline */}
                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5"
                    style={{
                      background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                    }}
                  />

                  {/* Timeline Items */}
                  <div className="space-y-8">
                    {timeline.map((item) => {
                      const Icon = item.icon
                      return (
                        <div key={item.year} className="relative pl-16">
                          {/* Year Badge */}
                          <div className="absolute left-0 top-0 w-12 h-12 rounded-full flex items-center justify-center"
                            style={{
                              background: item.color,
                              boxShadow: `0 0 20px ${item.color}40`,
                            }}
                          >
                            <Icon className="w-6 h-6 text-white" />
                          </div>

                          {/* Content Card */}
                          <div className="rounded-2xl p-5 transition-all duration-300 hover:translate-x-1"
                            style={{
                              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                              border: '1px solid rgba(255, 255, 255, 0.06)',
                            }}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-xs font-bold px-2 py-1 rounded-full"
                                style={{
                                  background: `${item.color}20`,
                                  color: item.color,
                                  border: `1px solid ${item.color}30`,
                                }}
                              >
                                {item.year}
                              </span>
                              <h3 className="text-white font-semibold">{item.title}</h3>
                            </div>
                            <p className="text-sm text-slate-400 leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Achievement Cards */}
                <div className="mt-10 p-6 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)',
                    border: '1px solid rgba(99, 102, 241, 0.2)',
                  }}
                >
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-primary" />
                    Leadership Excellence
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {achievements.map((item) => {
                      const Icon = item.icon
                      return (
                        <div key={item.label} className="text-center">
                          <div className="w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center"
                            style={{
                              background: `${item.color}15`,
                              border: `1px solid ${item.color}30`,
                            }}
                          >
                            <Icon className="w-6 h-6" style={{ color: item.color }} />
                          </div>
                          <p className="text-xl font-bold text-white">{item.value}</p>
                          <p className="text-xs text-slate-400">{item.label}</p>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* RIGHT SIDE - Managing Director Profile */}
            <FadeIn delay={0.2}>
              <div className="space-y-6">
                {/* Profile Card */}
                <div className="rounded-3xl p-8"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  {/* Profile Header */}
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-white mb-1">Mr. Faheem Shaikh</h2>
                    <p className="text-primary font-medium">Co-Founder & Managing Director</p>
                    <p className="text-sm text-slate-400 mt-1">Software Engineering</p>
                  </div>

                  {/* Biography */}
                  <div className="space-y-4 text-slate-300 leading-relaxed text-justify">
                    <p>
                      <strong className="text-white">Mr. Faheem Shaikh</strong> is a visionary entrepreneur and the driving
                      force behind <strong className="text-primary">Eonixa Dynamics Pvt. Ltd.</strong> With a solid academic
                      foundation in <strong className="text-white">Software Engineering</strong>, his professional journey is
                      a testament to perseverance, strategic foresight, and leadership excellence.
                    </p>

                    <p>
                      Over a career spanning nearly <strong className="text-white">15 years</strong>, Mr. Shaikh explored and
                      mastered diverse business landscapes. This extensive period of hands-on experience and industry exposure
                      honed his business acumen, resilience, and executive vision.
                    </p>

                    <p>
                      In <strong className="text-white">2009</strong>, he strategically entered the government projects and
                      procurement ecosystem, laying the foundational stone of what has now scaled into an enterprise valued at
                      approximately <strong className="text-white">₹150 Crores</strong>.
                    </p>

                    <p>
                      Under his astute leadership, Eonixa Dynamics has established highly credible and robust associations with
                      various government departments and public sector institutions across <strong className="text-white">Maharashtra
                      and pan-India</strong>. His journey reflects a powerful paradigm of entrepreneurial spirit, execution
                      excellence, and sustainable business growth.
                    </p>

                    {/* Quote */}
                    <div className="border-l-4 border-primary pl-4 py-3 my-6 rounded-r-lg"
                      style={{
                        background: 'rgba(99, 102, 241, 0.1)',
                      }}
                    >
                      <p className="text-white italic">
                        "Entrepreneurial spirit, execution excellence, and sustainable business growth form the foundation
                        of building lasting value and creating meaningful impact."
                      </p>
                      <p className="text-primary text-sm mt-2 font-medium">— Mr. Faheem Shaikh</p>
                    </div>

                    {/* Key Highlights Grid */}
                    <div className="grid grid-cols-2 gap-4 mt-6">
                      {highlights.map((item) => {
                        const Icon = item.icon
                        return (
                          <div key={item.label}
                            className="p-4 rounded-xl"
                            style={{
                              background: 'rgba(255, 255, 255, 0.03)',
                              border: '1px solid rgba(255, 255, 255, 0.06)',
                            }}
                          >
                            <Icon className="w-5 h-5 text-primary mb-2" />
                            <p className="text-lg font-bold text-white">{item.value}</p>
                            <p className="text-xs text-slate-400">{item.label}</p>
                          </div>
                        )
                      })}
                    </div>

                    {/* Signature */}
                    <div className="mt-8">
                      <img
                        src="/signature.png"
                        alt="Signature"
                        className="h-10 opacity-80"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Bottom Achievement Banner */}
                <div className="p-5 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      }}
                    >
                      <Award className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Built a ₹150 Crore Multi-Sector Enterprise</p>
                      <p className="text-sm text-slate-400">Technology • Infrastructure • Healthcare • Education</p>
                    </div>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </>
  )
}

export default MDMessage